import React, { useState, useEffect } from 'react';
import { Download, MessageCircle, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import pdf from '../assets/Adidoo_Profile_V10.pdf';

const FloatingActionButton = () => {
  const { isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIcon, setActiveIcon] = useState(0);

  // Alternate icons every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIcon((prev) => (prev === 0 ? 1 : 0));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleDownloadProfile = () => {
    // Replace with your actual PDF file path
    const link = document.createElement('a');
    link.href = pdf;
    link.download = 'Adidoo-Company-Profile.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsOpen(false);
  };

  const handleWhatsAppChat = () => {
    // WhatsApp number in international format without + or -
    const phoneNumber = '966551826405'; // +966 551826405
    const message = encodeURIComponent('Hello! I would like to know more about your services.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-8 z-50" style={{ [isRTL ? 'left' : 'right']: '2rem' }}>
      <div className="relative">
        {/* Action Buttons */}
        <div
          className={`absolute bottom-20 flex flex-col gap-4 transition-all duration-500 ${
            isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
          style={{ [isRTL ? 'left' : 'right']: '0' }}
        >
          {/* Download Button */}
          <div className="flex items-center gap-3" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <div
              className={`bg-white px-4 py-2 rounded-full shadow-lg whitespace-nowrap text-sm font-medium text-gray-700 transition-all duration-300 ${
                isOpen ? 'opacity-100 translate-x-0' : 'opacity-0'
              }`}
              style={{
                transform: isOpen ? 'translateX(0)' : isRTL ? 'translateX(-10px)' : 'translateX(10px)',
              }}
            >
              {isRTL ? 'تحميل ملف الشركة' : 'Download Profile'}
            </div>
            <button
              onClick={handleDownloadProfile}
              className="w-12 h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center group transform hover:scale-110"
              aria-label="Download Company Profile"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>

          {/* WhatsApp Button */}
          <div className="flex items-center gap-3" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <div
              className={`bg-white px-4 py-2 rounded-full shadow-lg whitespace-nowrap text-sm font-medium text-gray-700 transition-all duration-300 ${
                isOpen ? 'opacity-100 translate-x-0' : 'opacity-0'
              }`}
              style={{
                transform: isOpen ? 'translateX(0)' : isRTL ? 'translateX(-10px)' : 'translateX(10px)',
                transitionDelay: '50ms',
              }}
            >
              {isRTL ? 'تواصل عبر واتساب' : 'WhatsApp Chat'}
            </div>
            <button
              onClick={handleWhatsAppChat}
              className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center group transform hover:scale-110"
              aria-label="WhatsApp Chat"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main FAB Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center group transform hover:scale-110 relative overflow-hidden"
          aria-label="Quick Actions"
        >
          {/* Animated background pulse */}
          <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full animate-ping opacity-20"></div>
          
          {/* Icon container with smooth transition */}
          <div className="relative z-10">
            {isOpen ? (
              <X className="w-7 h-7 transition-all duration-300 transform rotate-0 group-hover:rotate-90" />
            ) : (
              <div className="relative w-7 h-7">
                {/* Download Icon */}
                <Download
                  className={`absolute inset-0 w-7 h-7 transition-all duration-500 ${
                    activeIcon === 0
                      ? 'opacity-100 rotate-0 scale-100'
                      : 'opacity-0 -rotate-90 scale-50'
                  }`}
                />
                {/* WhatsApp Icon */}
                <MessageCircle
                  className={`absolute inset-0 w-7 h-7 transition-all duration-500 ${
                    activeIcon === 1
                      ? 'opacity-100 rotate-0 scale-100'
                      : 'opacity-0 rotate-90 scale-50'
                  }`}
                />
              </div>
            )}
          </div>

          {/* Ripple effect on hover */}
          <span className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></span>
        </button>

        {/* Tooltip */}
        {!isOpen && (
          <div
            className={`absolute top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none`}
            style={{
              [isRTL ? 'right' : 'left']: isRTL ? '4.5rem' : 'auto',
              [isRTL ? 'left' : 'right']: isRTL ? 'auto' : '4.5rem',
            }}
          >
            {isRTL ? 'إجراءات سريعة' : 'Quick Actions'}
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 transform rotate-45`}
              style={{
                [isRTL ? 'left' : 'right']: '-4px',
              }}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingActionButton;